import * as THREE from 'three';
import type { Renderer } from '@/rendering/Renderer';
import type { SceneManager } from '@/rendering/SceneManager';
import {
  CameraViewMode,
  zoomForFovDegrees,
  type CameraManager,
  type OrbitState,
} from '@/rendering/CameraManager';
import type { EnvironmentManager } from '@/environment/EnvironmentManager';
import { serializeEnvironmentState, type EnvironmentState } from '@/environment/EnvironmentState';
import type { FloraWorld } from '@/flora/FloraWorld';
import type { FloraResolver } from '@/localization/FloraResolver';
import { getTerrainTemplate } from '@/terrain/TerrainTemplates';
import type { Terrain } from '@/terrain/Terrain';
import type { SurfaceCoverage } from '@/terrain/TerrainSurface';
import { groundCoverageInView } from './GroundCoverage';
import {
  captureBeauty,
  captureDepth,
  captureIdMap,
  depthRangeForBox,
  makeThumbnail,
  pixelsToPngBlob,
  type PassContext,
} from './CapturePasses';
import { nearestSizePreset, type ImageSize } from './ImageSpec';
import {
  buildSceneManifest,
  type ManifestCreatureInput,
  type ManifestSite,
  type ManifestStructureInput,
  type ProjectedPoint,
  type SceneManifest,
  type Vec3,
} from './SceneManifest';
import { assignSpeciesColors, faunaKeyId, siteObjectKeyId } from './SpeciesPalette';
import type { FaunaWorld } from '@/fauna/FaunaWorld';
import type { SiteObjectWorld } from '@/site/SiteObjectWorld';
import type { SiteObject } from '@/site/SiteObject';
import type { PropDefinition } from '@/site/PropCatalog';
import { propLibrary } from '@/site/PropLibrary';
import type { TagStore } from '@/semantics/TagStore';

/**
 * Ties the render passes to the live application state and produces one self-contained capture:
 * three pixel-aligned images plus the manifest that explains them.
 *
 * This is the only place where Three.js and the pure manifest layer meet. `SceneManifest` receives a
 * projection callback rather than a camera, so everything downstream of here stays testable without
 * a GPU.
 */

/** Serializable camera state, enough to restore the exact frame later. */
export interface CameraSnapshot {
  target: Vec3;
  distance: number;
  azimuth: number;
  polar: number;
  viewMode: CameraViewMode;
  /**
   * The lens the frame was shot on, as a vertical field of view.
   *
   * Optional because every shot taken before the lens could be changed was taken on the default one,
   * and a missing value has to keep meaning exactly that rather than becoming a zero-degree camera.
   */
  fovDegrees?: number;
}

export interface CaptureResult {
  size: ImageSize;
  beauty: Blob;
  idMap: Blob;
  depth: Blob;
  thumbnail: Blob;
  manifest: SceneManifest;
  camera: CameraSnapshot;
  environment: EnvironmentState;
  /** Species key colours used by the ID map — the prompt legend must match these exactly. */
  speciesColors: Map<string, string>;
}

export interface CaptureServiceDeps {
  renderer: Renderer;
  scene: SceneManager;
  camera: CameraManager;
  environment: EnvironmentManager;
  world: () => FloraWorld | null;
  /**
   * The site's flora resolver, so what a render is *told* matches what it was *shown*. Optional: a
   * capture service built without one describes plants against the catalog's reference climate.
   */
  resolver?: () => FloraResolver | null;
  /** Buildings and paving. Optional so existing test doubles keep satisfying the interface. */
  siteObjects?: () => SiteObjectWorld | null;
  /**
   * Placed creatures and figures. Optional for the same reason, and the omission is not free: a
   * capture built without this describes a frame containing a deer as a frame containing nothing,
   * which is what the enhancement pass then renders.
   */
  fauna?: () => FaunaWorld | null;
  /**
   * The site's semantic tags. Optional for the same reason: a capture without them is the capture
   * this service always produced, only less informed.
   */
  tags?: () => TagStore | null;
  /**
   * Resolve a site object's authored asset, so a prop reaches the manifest by name rather than as a
   * box. Defaults to the shipped prop library.
   */
  prop?: (object: SiteObject) => PropDefinition | undefined;
  terrain: () => Terrain | null;
  /**
   * Which document is open, so a shot can be matched back to the site it was taken of — see
   * `ManifestSite.id`. Returns null for a site that has never been saved, and is resolved per
   * capture rather than at construction because this service is built before the project menu is.
   */
  siteIdentity?: () => { id: string; name: string } | null;
  /** Live viewport size, used to derive a capture size with a matching aspect ratio. */
  viewport: () => { width: number; height: number };
}

export class CaptureService {
  constructor(private readonly deps: CaptureServiceDeps) {}

  /** Whether the active renderer can capture at all (a mock renderer in tests cannot). */
  get supported(): boolean {
    return typeof this.deps.renderer.renderToPixels === 'function';
  }

  /**
   * The capture size for "Fit": the documented output size closest to the viewport's aspect ratio.
   *
   * Snapping to a listed size rather than deriving an exact match keeps every capture directly
   * usable as an enhancement input — see `nearestSizePreset`.
   */
  suggestedSize(): ImageSize {
    const { width, height } = this.deps.viewport();
    return nearestSizePreset(width, height);
  }

  /**
   * Render all three passes and build the manifest.
   *
   * Async only because PNG encoding is; the three renders happen back to back on one frame so the
   * scene cannot change between the beauty pass and the ID map.
   */
  async capture(size: ImageSize): Promise<CaptureResult> {
    const { scene, camera: cameraManager, environment, renderer } = this.deps;
    const world = this.deps.world();
    const terrain = this.deps.terrain();

    const camera = this.captureCamera(size);
    const context: PassContext = {
      renderer,
      scene: scene.scene,
      camera,
      size,
      overlayRoot: scene.overlayRoot,
      floraRoot: scene.floraRoot,
      siteRoot: scene.siteRoot,
      terrainRoot: scene.terrainRoot,
    };

    const instances = world?.all() ?? [];
    const siteWorld = this.deps.siteObjects?.() ?? null;
    const faunaWorld = this.deps.fauna?.() ?? null;
    const structures = this.structureInputs(siteWorld, terrain);
    const creatures = this.creatureInputs(faunaWorld, terrain);

    // Plants, buildings and creatures draw from one palette, so nothing can decode as another class.
    const speciesColors = assignSpeciesColors([
      ...instances.map((i) => i.floraDefinitionId),
      ...structures.map((s) => siteObjectKeyId(s.object.id)),
      ...creatures.map((c) => faunaKeyId(c.definition.id)),
    ]);

    const beautyPixels = captureBeauty(context);
    const idMapPixels = captureIdMap(context, speciesColors, (object) =>
      this.keyIdFor(object, world, faunaWorld),
    );
    const depthPixels = captureDepth(context, this.depthRange(camera));

    const [beauty, idMap, depth] = await Promise.all([
      pixelsToPngBlob(beautyPixels, size.width, size.height),
      pixelsToPngBlob(idMapPixels, size.width, size.height),
      pixelsToPngBlob(depthPixels, size.width, size.height),
    ]);
    const thumbnail = await makeThumbnail(beauty);

    const manifest = buildSceneManifest({
      plants: instances.map((instance) => ({
        instance,
        definition: (world as FloraWorld).definitionFor(instance),
      })),
      structures,
      creatures,
      environment: environment.state,
      effectiveDayOfYear: environment.effectiveDayOfYear(),
      camera: this.cameraDescription(camera, cameraManager.orbit),
      image: size,
      site: terrain ? this.siteDescription(terrain) : null,
      project: this.projector(camera),
      speciesColors,
      // Every tag on the document, not only the ones the camera can see: the manifest decides what is
      // relevant to this frame, and it is the only thing that knows where the camera is pointing.
      tags: this.deps.tags?.()?.all() ?? [],
      prop: this.deps.prop ?? ((object) => propLibrary.definitionFor(object)),
      // Measured through this camera, not across the whole site — see `groundCoverageInView`.
      groundCoverage: this.groundInView(terrain, camera),
      ...(this.deps.resolver?.() ? { resolver: this.deps.resolver() as FloraResolver } : {}),
    });

    return {
      size,
      beauty,
      idMap,
      depth,
      thumbnail,
      manifest,
      camera: this.cameraSnapshot(),
      environment: serializeEnvironmentState(environment.state),
      speciesColors,
    };
  }

  /**
   * The site block of the manifest: what the ground is, and — when the document has been saved —
   * which document it is. The id and name are omitted rather than written empty, so a shot taken of
   * an unsaved draft is distinguishable from one whose site is genuinely unknown.
   */
  private siteDescription(terrain: Terrain): ManifestSite {
    const identity = this.deps.siteIdentity?.() ?? null;
    return {
      templateName: getTerrainTemplate(terrain.params.templateId).name,
      widthMeters: terrain.widthMeters,
      lengthMeters: terrain.lengthMeters,
      ...(identity ? { id: identity.id, name: identity.name } : {}),
    };
  }

  /** Current camera state, enough to return to this exact frame. */
  cameraSnapshot(): CameraSnapshot {
    const orbit = this.deps.camera.orbit;
    return {
      target: { x: orbit.target.x, y: orbit.target.y, z: orbit.target.z },
      distance: orbit.distance,
      azimuth: orbit.azimuth,
      polar: orbit.polar,
      viewMode: this.deps.camera.viewMode,
      fovDegrees: this.deps.camera.fovDegrees,
    };
  }

  /** Restore a saved frame: camera orbit, view mode, and the lens it was shot on. */
  restoreCamera(snapshot: CameraSnapshot): void {
    const manager = this.deps.camera;
    const orbit: OrbitState = manager.orbit;
    orbit.target.set(snapshot.target.x, snapshot.target.y, snapshot.target.z);
    orbit.distance = snapshot.distance;
    orbit.azimuth = snapshot.azimuth;
    orbit.polar = snapshot.polar;
    if (snapshot.fovDegrees !== undefined) manager.setLensZoom(zoomForFovDegrees(snapshot.fovDegrees));
    manager.setViewMode(snapshot.viewMode);
    manager.applyOrbit();
  }

  /**
   * A camera matching the capture's aspect ratio rather than the viewport's.
   *
   * The live camera is cloned instead of mutated: an exception mid-capture would otherwise leave the
   * on-screen view stretched, and photo mode's letterbox already shows the user this exact frame.
   */
  private captureCamera(size: ImageSize): THREE.Camera {
    const aspect = size.width / Math.max(1, size.height);
    const active = this.deps.camera.activeCamera;

    if (active instanceof THREE.PerspectiveCamera) {
      const clone = active.clone();
      clone.aspect = aspect;
      clone.updateProjectionMatrix();
      clone.updateMatrixWorld(true);
      return clone;
    }
    if (active instanceof THREE.OrthographicCamera) {
      const clone = active.clone();
      const halfHeight = (active.top - active.bottom) / 2;
      const halfWidth = halfHeight * aspect;
      clone.left = -halfWidth;
      clone.right = halfWidth;
      clone.top = halfHeight;
      clone.bottom = -halfHeight;
      clone.updateProjectionMatrix();
      clone.updateMatrixWorld(true);
      return clone;
    }
    return active;
  }

  /**
   * World → normalised-device projection for the manifest.
   *
   * Depth is measured as true distance from the camera rather than NDC z, because the manifest talks
   * in meters and NDC z is non-linear enough to make "foreground" meaningless.
   */
  private projector(camera: THREE.Camera): (world: Vec3) => ProjectedPoint {
    const origin = camera.getWorldPosition(new THREE.Vector3());
    const inverse = camera.matrixWorldInverse.clone();
    const point = new THREE.Vector3();

    return (world) => {
      point.set(world.x, world.y, world.z);
      const distanceMeters = point.distanceTo(origin);
      // Camera space looks down -Z, so anything at z >= 0 is at or behind the lens.
      const viewZ = point.clone().applyMatrix4(inverse).z;
      const projected = point.project(camera);
      return {
        x: projected.x,
        y: projected.y,
        distanceMeters,
        behindCamera: viewZ >= 0,
      };
    };
  }

  private cameraDescription(
    camera: THREE.Camera,
    orbit: OrbitState,
  ): { fieldOfViewDegrees: number; heightMeters: number; pitchDegrees: number; distanceMeters: number } {
    const position = camera.getWorldPosition(new THREE.Vector3());
    const forward = camera.getWorldDirection(new THREE.Vector3());
    return {
      fieldOfViewDegrees: camera instanceof THREE.PerspectiveCamera ? camera.fov : 0,
      heightMeters: position.y,
      // Negative when the camera looks down at the site.
      pitchDegrees: THREE.MathUtils.radToDeg(Math.asin(THREE.MathUtils.clamp(forward.y, -1, 1))),
      distanceMeters: orbit.distance,
    };
  }

  /**
   * The ground materials in frame, as shares of the visible ground.
   *
   * Resolves the world — surface map, north rotation, heightfield, camera — and hands the arithmetic
   * to {@link groundCoverageInView}, which stays free of Three.js and of terrain for the same reason
   * `SceneManifest` does.
   */
  private groundInView(terrain: Terrain | null, camera: THREE.Camera): SurfaceCoverage | null {
    if (!terrain) return null;
    const position = camera.getWorldPosition(new THREE.Vector3());
    const world = new THREE.Vector2();
    return groundCoverageInView({
      map: terrain.surface,
      toWorld: (x, z) => {
        terrain.toWorld(x, z, world);
        return { x: world.x, z: world.y };
      },
      heightAt: (x, z) => terrain.sampleHeight(x, z),
      project: this.projector(camera),
      cameraPosition: { x: position.x, y: position.y, z: position.z },
    });
  }

  /** Fit the depth pass's frustum to the terrain, planting, and buildings actually present. */
  private depthRange(camera: THREE.Camera): { nearMeters: number; farMeters: number } {
    const box = new THREE.Box3();
    box.expandByObject(this.deps.scene.terrainRoot);
    box.expandByObject(this.deps.scene.floraRoot);
    box.expandByObject(this.deps.scene.siteRoot);
    if (box.isEmpty()) return { nearMeters: 1, farMeters: 200 };
    return depthRangeForBox(camera, box);
  }

  /**
   * Site objects paired with the ground height they sit at, which is what the manifest needs to
   * project them. The world places an object at the high point under its footprint, so the same
   * sampling is repeated here rather than read back off the view — the manifest must describe the
   * document, not the scene graph.
   */
  private structureInputs(
    siteWorld: SiteObjectWorld | null,
    terrain: Terrain | null,
  ): ManifestStructureInput[] {
    if (!siteWorld) return [];
    return siteWorld.all().map((object) => ({
      object,
      baseY: (terrain?.sampleHeight(object.position.x, object.position.z) ?? 0) + object.position.y,
    }));
  }

  /**
   * Placed creatures paired with the absolute ground height they stand at.
   *
   * The instance's own `position.y` is an offset the world adds a ground sample to — 0 on the lawn,
   * a perch's height on a branch, the record's hover height in the air — so the same addition is
   * repeated here. Read off the record rather than back off the scene graph, for the reason
   * `structureInputs` gives: the manifest describes the document.
   */
  private creatureInputs(
    faunaWorld: FaunaWorld | null,
    terrain: Terrain | null,
  ): ManifestCreatureInput[] {
    if (!faunaWorld) return [];
    const inputs: ManifestCreatureInput[] = [];
    for (const instance of faunaWorld.all()) {
      const definition = faunaWorld.definitionFor(instance);
      // A record this build no longer carries draws nothing, so describing it would tell the model
      // about a silhouette that is not in the frame.
      if (!definition) continue;
      const ground = terrain?.sampleHeight(instance.position.x, instance.position.z) ?? 0;
      inputs.push({
        instance,
        definition,
        baseY: ground + instance.position.y + instance.heightOffsetMeters,
      });
    }
    return inputs;
  }

  /**
   * Walk up from a mesh to the view group that owns it and resolve its palette key.
   *
   * Flora views are built by `FloraFactory` as a group carrying `userData.floraInstanceId`, with the
   * `structure` / `foliage` / `accent` meshes beneath; site object views carry
   * `userData.siteObjectId` and fauna views `userData.faunaInstanceId`. All three are keyed off the
   * document, never off mesh names.
   *
   * A mesh that resolves to nothing is left black in the ID map, which the prompt tells the model
   * means "ground or sky". That is why fauna had to be added here and not only to the manifest:
   * until it was, every placed creature was painted as an explicit statement that nothing was there.
   */
  private keyIdFor(
    object: THREE.Object3D,
    world: FloraWorld | null,
    faunaWorld: FaunaWorld | null,
  ): string | undefined {
    let node: THREE.Object3D | null = object;
    while (node) {
      const siteObjectId = node.userData.siteObjectId as string | undefined;
      if (siteObjectId !== undefined) return siteObjectKeyId(siteObjectId);

      const instanceId = node.userData.floraInstanceId as string | undefined;
      if (instanceId !== undefined) return world?.get(instanceId)?.floraDefinitionId;

      const faunaInstanceId = node.userData.faunaInstanceId as string | undefined;
      if (faunaInstanceId !== undefined) {
        const instance = faunaWorld?.get(faunaInstanceId);
        return instance ? faunaKeyId(instance.faunaDefinitionId) : undefined;
      }

      node = node.parent;
    }
    return undefined;
  }
}
